import { createContext } from 'react';
import { decorate, observable, reaction } from 'mobx';
import moment from 'moment';

import api from '../api';
import * as StorageKeys from '../constants/storageKeys';
import * as fn from '../utilities/_functions';

export class Auth {
    currentUser = null;
    loggedInUser = null;
    currentTenant = null;
    switchPin = null;
    isLocked = false;
    isLockReady = false;
    isSwitchPinReady = false;
    isSwitchPinActivated = false;
    tokenRenewalTimeout = null;
    isProcessing = false;
    cancelAuthorizeStart = null;
    cancelTenants = null;
    cancelTenantsReload = null;
    cancelTenantsSwitch = null;
    cancelSettings = null;
    cancelSettingsReload = null;
    cancelSettingsSwitch = null;
    cancelUserGet = null;
    cancelUserSwitch = null;
    currentUserDisposer = null;
    currentTenantDisposer = null;
    loggedInUserDisposer = null;

    constructor() {
        this.currentUserDisposer = reaction(
            () => this.currentUser, (user) => {
                if (user) {
                    window.localStorage.setItem(StorageKeys.CURRENT_USER, JSON.stringify(user));
                } else {
                    window.localStorage.removeItem(StorageKeys.CURRENT_USER);
                }
            }
        );
        this.currentTenantDisposer = reaction(
            () => this.currentTenant, (tenant) => {
                if (tenant) {
                    window.localStorage.setItem(StorageKeys.CURRENT_TENANT, JSON.stringify(tenant));
                } else {
                    window.localStorage.removeItem(StorageKeys.CURRENT_TENANT);
                }
            }
        );
        this.loggedInUserDisposer = reaction(
            () => this.loggedInUser, (user) => {
                if (user) {
                    window.localStorage.setItem(StorageKeys.LOGGED_IN_USER, JSON.stringify(user));
                } else {
                    window.localStorage.removeItem(StorageKeys.LOGGED_IN_USER);
                }
            }
        )

        this.currentUser = JSON.parse(window.localStorage.getItem(StorageKeys.CURRENT_USER));
        this.currentTenant = JSON.parse(window.localStorage.getItem(StorageKeys.CURRENT_TENANT));
        this.loggedInUser = JSON.parse(window.localStorage.getItem(StorageKeys.LOGGED_IN_USER));
    }

    handleAuthentication = async (user, authResult) => {
        const isFailover = !!process.env.REACT_APP_IS_FAILOVER;
        this.isProcessing = true;
        const hostParts = window.location.host.split('.');
        let subdomain = hostParts[0];

        if (process.env.NODE_ENV === 'development') {
            subdomain = process.env.REACT_APP_DEBUG_ORG_SUBDOMAIN;
        }

        // console.log('isFailover', isFailover);
        // console.log('subdomain', subdomain);
        // console.log('identity', user.sub);

        const { data: authorization } = isFailover ? await api.Authorize.failover({ subdomain, identity: user.sub }, (c) => { this.cancelAuthorizeStart = c; }) : await api.Authorize.start({ subdomain, identity: user.sub }, (c) => { this.cancelAuthorizeStart = c; });

        // console.log('authorization', authorization);
        this.setAuthorization(authorization);

        // Authorization has defaultTenant embedded in it, so user.defaultTenantId is the default tenant id
        const { data: loggedInUser } = await api.Users.get(authorization.id, (c) => { this.cancelUserGet = c; });

        // console.log('loggedInUser', loggedInUser);
        this.currentUser = loggedInUser;
        this.loggedInUser = loggedInUser;

        // the currentUser.defaultTenantId is only used in login - it signals which tenant is the default tenant for this user
        const { data: tenant } = await api.Tenants.get(this.currentUser.defaultTenantId, false, (c) => { this.cancelTenants = c; });

        console.log('tenant', tenant);
        this.currentTenant = tenant;

        // console.log('handleAuthentication');
        const { data: settings } = await api.Settings.all(this.currentUser.id, (c) => { this.cancelSettings = c; });
        this.setSettings(settings);

        moment().tz(tenant.timeZoneId);

        this.setSession(authResult);

        // Pretend that User is set with "OfficeLoginOnly" = true, but this workstation has not been registered
        // UI should prompt for temp password, before proceeding to workstation registration
        console.log("!!!! User only allow login from office", this.currentUser.officeLoginOnly);
        if (!this.currentUser.officeLoginOnly) {
            this.isProcessing = false;
            return;
        }

        // this user can only login from office workstation
        var workstationKey = window.localStorage.getItem(StorageKeys.WORKSTATION_KEY);
        if (!!workstationKey) {
            // Has this workstation been registered (publickey/privatekey created?)

            const workstationAuthentication = await this.authenticateOfficeWorkstation(workstationKey);
            console.log("!!!!! Office workstation authenticated", workstationAuthentication.workstation);
        }
        else {
            // *** user is "OfficeLoginOnly" and this workstation is not registered, 
            // we should call genki api to generate a simple random passcode, send to the owner
            // and here we need to prompt the user for this temporary passcode

            var resp = await api.OfficeWorkstationEnrollment.request({ deviceName: "TEST_STATION" }, (c) => { this.cancelSettings = c; });
            console.log("#### You should not see this but the passcode is ",  JSON.stringify(resp.data));
            const passcode =
                window.prompt(
                    "This workstation is not registered.\n\n" +
                    "A temporary authorization code has been sent " +
                    "to the clinic owner.\n\n" +
                    "Please enter the authorization code:"
                );
            await this.registerOfficeWorkstation({ enrollmentId: resp.data.enrollmentId, code: passcode })
        }

        this.isProcessing = false;
    }

    base64ToBase64Url = (value) => {
        return value
            .replace(/\+/g, "-")
            .replace(/\//g, "_")
            .replace(/=+$/, "");
    }

    authenticateOfficeWorkstation = async (workstationKey) => {
        try {
            if (!window.PublicKeyCredential) {
                throw new Error(
                    "WebAuthn is not supported by this browser."
                );
            }

            //
            // 1. Ask .NET to create
            //    an authentication challenge.
            //
            // Expected response:
            // {
            //     noRegisteredWorkstations: false,
            //     authenticationId: "...",
            //     options: { ... }
            // }
            //
            const start = await api.OfficeWorkstation.authenticateStart({ workstationKey });
            console.log(
                "!!!!! Office workstation authentication/start",
                start
            );

            //
            // There are no registered
            // OfficeWorkstation credentials
            // for this tenant.
            //
            if (start.data.noRegisteredWorkstations) {
                console.log("!!!!! No OfficeWorkstation credentials are registered.");
                window.localStorage.removeItem(StorageKeys.WORKSTATION_KEY);
                window.location.href = "/logout";

                return {
                    success: false,
                    noRegisteredWorkstations: true
                };
            }

            //
            // 2. Convert WebAuthn
            //    binary fields from Base64
            //    to Base64URL.
            //
            //
            // Authentication has:
            //
            // challenge
            // allowCredentials[].id
            //
            const options = {
                ...start.data.options,

                challenge:
                    this.base64ToBase64Url(start.data.options.challenge),

                allowCredentials:
                    (
                        start.data.options
                            .allowCredentials ||
                        []
                    ).map(
                        x => ({
                            ...x,
                            id: this.base64ToBase64Url(
                                x.id
                            )
                        })
                    )
            };
            console.log("!!!!! Authentication request options", options);

            //
            // Authentication uses:
            //
            // parseRequestOptionsFromJSON()
            //
            // Registration uses:
            //
            // parseCreationOptionsFromJSON()
            //
            const publicKey = window
                .PublicKeyCredential
                .parseRequestOptionsFromJSON(options);
            console.log(
                "!!!!! Before navigator.credentials.get()",
                publicKey
            );

            //
            // 3. Ask the platform authenticator
            //    to prove possession of one
            //    of the allowed credentials.
            //
            const credential = await navigator.credentials.get({ publicKey });
            console.log(">>>>>> Authentication credential", credential);

            if (!credential) {
                throw new Error("WebAuthn authentication was cancelled.");
            }

            //
            // 4. Send the assertion back
            //    to .NET.
            //
            const complete =
                await api.OfficeWorkstation.authenticateComplete({
                    authenticationId:
                        start.data.authenticationId,

                    credential:
                        typeof credential.toJSON ===
                            "function"
                            ? credential.toJSON()
                            : credential
                });

            console.log("!!!!! Office workstation authentication/complete", complete);
            return {
                success: true,
                ...complete.data
            };
        }
        catch (e) {
            window.localStorage.removeItem(StorageKeys.WORKSTATION_KEY);
            window.location.href = "/logout";
            console.error("Office workstation authentication failed", e);
            //alert(e);

            return {
                success: false,
                error: e
            };
        }
    }

    registerOfficeWorkstation = async ({
        enrollmentId,
        code
    }) => {
        //
        // 1. Ask .NET to validate the enrollment/code and
        //    create WebAuthn registration options.
        //
        const start = await api.OfficeWorkstationEnrollment.registerStart({ enrollmentId, code });
        //
        // 2. Convert JSON coming from Fido2NetLib into
        //    the objects navigator.credentials.create() expects.
        //
        console.log('!!!!! Start Resp', start);

        try {
            if (!window.PublicKeyCredential) {
                alert("PublicKeyCredential not supported");
                return;
            }

            const options = {
                ...start.data.options,

                challenge: this.base64ToBase64Url(
                    start.data.options.challenge
                ),

                user: {
                    ...start.data.options.user,
                    id: this.base64ToBase64Url(
                        start.data.options.user.id
                    )
                },

                excludeCredentials:
                    (start.data.options.excludeCredentials || [])
                        .map(x => ({
                            ...x,
                            id: this.base64ToBase64Url(x.id)
                        }))
            };

            console.log("!!!! before creating public key:", options);

            const publicKey = window.PublicKeyCredential
                .parseCreationOptionsFromJSON(
                    options
                );

            //
            // 3. This invokes Windows Hello / macOS WebAuthn.
            //
            console.log('!!!!! Creating credentials', publicKey);
            const credential =
                await navigator.credentials.create({
                    publicKey
                });
            console.log('!!!!! Created credentials', credential);

            if (!credential) {
                throw new Error(
                    "WebAuthn registration was cancelled."
                );
            }

            //
            // 4. Send the resulting attestation back to .NET.
            //
            const result = await api.OfficeWorkstationEnrollment.registerComplete({ registrationId: start.data.registrationId, credential });
            console.log('!!!!! Completed result', result);

            window.localStorage.setItem(StorageKeys.WORKSTATION_KEY, result.data.workstation.id);
            return result;
        }
        catch (e) {
            alert(e);
            console.error(e);
            window.location.href = "/logout";
        }

        return null;
    }

    activateLockScreen = () => {
        this.isLockReady = true;
        this.isLocked = true;
        this.isSwitchPinActivated = false;
        return Promise.resolve();
    }

    deactivateLockScreen = () => {
        this.isLockReady = false;
        this.isLocked = false;
        this.isSwitchPinActivated = false;
        this.currentUser = JSON.parse(window.localStorage.getItem(StorageKeys.CURRENT_USER));

        return Promise.resolve();
    }

    activateSwitchPinScreen = () => {
        this.isSwitchPinReady = true;
        this.isSwitchPinActivated = true;
        return Promise.resolve();
    }

    deactivateSwitchPinScreen = () => {
        if (this.isSwitchPinActivated) {
            this.isSwitchPinActivated = false;
        }
        return Promise.resolve();
    }

    refresh = () => {
        this.currentUser = JSON.parse(window.localStorage.getItem(StorageKeys.CURRENT_USER));
        this.loggedInUser = JSON.parse(window.localStorage.getItem(StorageKeys.LOGGED_IN_USER));
    }

    reset() {
        this.switchPin = null;
        this.currentUser = null;
        this.loggedInUser = null;
        this.isLocked = false;
        this.isLockReady = false;
        this.isSwitchPinReady = false;
        this.isSwitchPinActivated = false;
        this.isProcessing = false;

        if (fn.isFunction(this.cancelAuthorizeStart)) {
            this.cancelAuthorizeStart = null;
        }

        if (fn.isFunction(this.cancelTenants)) {
            this.cancelTenants();
            this.cancelTenants = null;
        }

        if (fn.isFunction(this.cancelTenantsReload)) {
            this.cancelTenantsReload();
            this.cancelTenantsReload = null;
        }

        if (fn.isFunction(this.cancelTenantsSwitch)) {
            this.cancelTenantsSwitch();
            this.cancelTenantsSwitch = null;
        }

        if (fn.isFunction(this.cancelSettings)) {
            this.cancelSettings();
            this.cancelSettings = null;
        }

        if (fn.isFunction(this.cancelSettingsReload)) {
            this.cancelSettingsReload();
            this.cancelSettingsReload = null;
        }

        if (fn.isFunction(this.cancelSettingsSwitch)) {
            this.cancelSettingsSwitch();
            this.cancelSettingsSwitch = null;
        }

        if (fn.isFunction(this.cancelUserGet)) {
            this.cancelUserGet();
            this.cancelUserGet = null;
        }

        if (fn.isFunction(this.cancelUserSwitch)) {
            this.cancelUserSwitch();
            this.cancelUserSwitch = null;
        }

        if (fn.isFunction(this.currentUserDisposer)) {
            this.currentUserDisposer();
            this.currentUserDisposer = null;
        }

        if (fn.isFunction(this.currentTenantDisposer)) {
            this.currentTenantDisposer();
            this.currentTenantDisposer = null;
        }

        if (fn.isFunction(this.loggedInUserDisposer)) {
            this.loggedInUserDisposer();
            this.loggedInUserDisposer = null;
        }
    }

    setAuthorization = (authorization) => {
        // const clientIp = window.localStorage.getItem(StorageKeys.CLIENT_IP);
        // const updated = (clientIp !== authorization.ip || (this.currentUser && this.currentUser.id !== authorization.id));

        //        if (updated) {
        window.localStorage.setItem(StorageKeys.CLIENT_IP, authorization.ip);
        window.localStorage.setItem(StorageKeys.AUTHORIZATION_KEY, authorization.key);
        window.localStorage.setItem(StorageKeys.AUTHORIZATION_TOKEN, authorization.token);
        //      }
        //    return updated;
    }

    setSettings = (settings) => {
        window.localStorage.setItem(StorageKeys.SETTINGS, JSON.stringify(settings));
    }

    setSession = (authResult) => {
        const now = new Date();
        const tokenExpiresIn = parseInt(process.env.REACT_APP_AUTH0_SESSION_EXPIRY);
        const tokenExpiresAt = (!!authResult && !!authResult.exp ? new Date((authResult.exp * 1000)).getTime() : now.getTime() + tokenExpiresIn);
        const nightlyLogoutTime = new Date(new Date().setHours(3, 59, 59));
        const nightlyLogoutAt = now.getTime() >= nightlyLogoutTime.getTime() ? nightlyLogoutTime.setDate(now.getDate() + 1) : nightlyLogoutTime.getTime();
        const expiresAt = Math.min(tokenExpiresAt, nightlyLogoutAt);

        window.localStorage.setItem(StorageKeys.EXPIRES_AT, JSON.stringify(expiresAt));
    }

    clearSession = () => {
        for (let item of StorageKeys.CLEAR_FIELDS) {
            window.localStorage.removeItem(item);
        }
    }

    get isSessionActive() {
        const expiresAtJson = window.localStorage.getItem(StorageKeys.EXPIRES_AT);

        // if (expiresAtJson && this.currentUser && this.currentTenant) {  // TODO: Is this causing the multiple refresh???
        if (expiresAtJson) {
            const expiresAt = JSON.parse(expiresAtJson);
            const isExpired = new Date().getTime() >= expiresAt;

            if (expiresAt && isExpired) {
                return false;
            }

            return !isExpired;
        }

        return false;
    }

    get isSessionValid() {
        if (!this.isSessionActive) {
            const expiresAtJson = window.localStorage.getItem(StorageKeys.EXPIRES_AT);
            const currentUser = window.localStorage.getItem(StorageKeys.CURRENT_USER);
            const currentTenant = window.localStorage.getItem(StorageKeys.CURRENT_TENANT);
            const loggedInUser = window.localStorage.getItem(StorageKeys.LOGGED_IN_USER);
            const isValid = !expiresAtJson && !currentUser && !currentTenant && !loggedInUser;

            return isValid;
        }

        return true;
    }

    get isImpersonating() {
        if (this.currentUser && this.loggedInUser) {
            return (this.currentUser.id !== this.loggedInUser.id);
        }
        return false;
    }
}

decorate(Auth, {
    currentUser: observable.deep,
    currentTenant: observable.deep,
    loggedInUser: observable,
    switchPin: observable,
    isLocked: observable,
    isLockReady: observable,
    isSwitchPinActivated: observable,
    isSwitchPinReady: observable,
    isProcessing: observable,
    //isSessionActive: computed, // after upgraded to react-router v6, it returns stale values, which appeared to be the right behavior
})

export default createContext(new Auth());
